import { useMemo, useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Grid, MenuItem, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";

// Strip any directory / preamble in front of a source file so only the file
// name is shown in the UI (e.g. "kb_01.../FAQ.md" -> "FAQ.md"). The full value
// is kept in the data so the API still receives the original path.
const getSourceFileDisplayName = path => {
  if (!path) {
    return "";
  }

  const normalized = String(path).replace(/\\/g, "/");
  const parts = normalized.split("/");

  return parts[parts.length - 1] || normalized;
};

const buildSourceFileChoices = sourceFiles => {
  const unique = Array.from(new Set((sourceFiles || []).filter(Boolean)));

  return unique.length > 0 ? unique : ["FAQ.md"];
};

const buildInitialEntry = (entryRecord, sourceFileChoices) => {
  const record = entryRecord || {};
  const recordIsNew = record.id == null;

  const resolvedSourceFile = recordIsNew
    ? sourceFileChoices.includes(record.sourceFile)
      ? record.sourceFile
      : sourceFileChoices[0]
    : (record.sourceFile ?? sourceFileChoices[0] ?? "FAQ.md");

  return {
    id: record.id ?? null,
    topic: record.topic ?? "",
    clarification: record.clarification ?? "",
    sourceFile: resolvedSourceFile,
    lineNumber: record.lineNumber ?? "",
  };
};

const BotpressMemberVerifiedAnswersDialog = ({
  open,
  entryRecord,
  sourceFiles = [],
  dialogTitle = "Frequently Asked Question",
  okMessage = "Ok",
  cancelMessage = "Cancel",
  deleteMessage = "Delete",
  maxWidth = "md",
  fullWidth = true,
  onAccept,
  onDelete,
  onClose,
}) => {
  const theme = useTheme();

  // De-duped list of source files; fall back to a default when no documents
  // were found so a new entry always has something to point at.
  const sourceFileChoices = useMemo(() => buildSourceFileChoices(sourceFiles), [sourceFiles]);

  // The dialog is mounted fresh each time it opens, so seeding state once here
  // is correct and avoids an effect that could reset the fields mid-typing.
  const [editedEntry, setEditedEntry] = useState(() =>
    buildInitialEntry(entryRecord, buildSourceFileChoices(sourceFiles)),
  );

  const updateField = (fieldName, value) => {
    setEditedEntry(previousEntry => ({
      ...previousEntry,
      [fieldName]: value,
    }));
  };

  const formHasError = !editedEntry.topic.trim() || !editedEntry.clarification.trim();

  const onAcceptEntry = () => {
    if (formHasError) {
      return;
    }

    onAccept({
      ...editedEntry,
      topic: editedEntry.topic.trim(),
      clarification: editedEntry.clarification.trim(),
      sourceFile: editedEntry.sourceFile || sourceFileChoices[0] || "FAQ.md",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onClose}>{dialogTitle}</DialogTitle>

      <DialogContent>
        <Grid spacing={1} container size={{ xs: 12 }}>
          <Grid item container size={{ xs: 6 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Topic or Question"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"mvaTopic"}
              value={editedEntry.topic}
              onChange={event => updateField("topic", event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 6 }}>
            <TextField
              select
              sx={{ width: "100%" }}
              label={"Source File"}
              variant="outlined"
              size="small"
              margin="dense"
              InputLabelProps={{ shrink: true }}
              value={editedEntry.sourceFile}
              onChange={event => updateField("sourceFile", event.target.value)}
            >
              {sourceFileChoices.map(choice => (
                <MenuItem key={choice} value={choice}>
                  {getSourceFileDisplayName(choice)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Clarification"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"mvaClarification"}
              rowCount={4}
              multiline={true}
              value={editedEntry.clarification}
              onChange={event => updateField("clarification", event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Box
          sx={{
            paddingRight: theme.spacing(2),
            paddingBottom: 0,
            display: "flex",
            flexDirection: "row",
            userSelect: "none",
            webkitUserSelect: "none",
            position: "relative",
            flexGrow: 1,
          }}
        >
          {editedEntry.id != null && onDelete != null && (
            <OmniResponsiveButton
              icon={<DeleteIcon />}
              onClick={() => onDelete(editedEntry)}
              buttonText={deleteMessage}
              tooltipTitle=""
            />
          )}
        </Box>

        <Box
          sx={{
            flexGrow: 0,
            flexShrink: 1,
          }}
        >
          {cancelMessage && (
            <Button
              onClick={onClose}
              color="secondary"
              variant="contained"
              sx={{ marginRight: theme.spacing(2) }}
            >
              {cancelMessage}
            </Button>
          )}

          <Button
            onClick={onAcceptEntry}
            color="secondary"
            variant="contained"
            disabled={formHasError}
          >
            {okMessage}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BotpressMemberVerifiedAnswersDialog;
